/**
 * Step fragments shared by every embroidery walkthrough.
 *
 * A tutorial is its own lesson — the middle of it is written against one piece
 * of artwork and can't be generalised without gutting the copy. But the parts
 * either side are the same whatever you're stitching: getting a design loaded
 * at the start, and turning it into a file at the end. Those live here so a new
 * lesson only has to write the part that's actually new.
 */

/** The finished size of the stitch-out, highlighted as a pair. */
export const EXPORT_SIZE_TARGETS = [
  '[data-tutorial="export-width"]',
  '[data-tutorial="export-height"]',
];

/** The three stitch-type buttons, in the order the toolbar shows them. */
export const STITCH_TYPE_TARGETS = [
  '[data-tutorial="stitch-type-auto_fill"]',
  '[data-tutorial="stitch-type-satin"]',
  '[data-tutorial="stitch-type-running"]',
];

/**
 * Runs before a design has been chosen, so it can't mention one.
 * What the learner is about to be taught is stated by the lesson itself, once
 * the picker has settled which lesson that is.
 */
export const PRELUDE_STEPS = [
  {
    id: 'emb-welcome',
    title: 'Make Your First Embroidery File',
    description: 'Pick a practice design and I\'ll walk you through it click by click — all the way to a **stitch file your machine can read**.',
    target: null,
    position: 'center',
    showNext: true,
    showSkip: true,
    skipText: 'Skip',
    nextText: 'Let\'s go',
    encourageNext: true,
  },
  {
    id: 'emb-pick-design',
    title: 'Pick Your Practice Design',
    description: 'Choose a design to learn on — I\'ll load it for you.',
    target: null,
    position: 'center',
    picker: true,
    waitForAction: 'file-uploaded',
    autoAdvance: true,
  },
  {
    id: 'emb-processing',
    title: 'Finding Your Colours',
    description: 'The app is splitting your image into colour layers. Hang tight — I\'ll carry on when the conversion screen is ready.',
    target: null,
    position: 'center',
    // Overlay hides while this is up so the converter's own loading UI shows.
    waiting: true,
  },
  {
    id: 'emb-converter-colors',
    title: 'Your Thread Colours',
    description: 'These are the **thread colours** the converter picked — each swatch is one thread. Check they look right, then we\'ll open the Stitch Editor.',
    target: '[data-tutorial="color-layers"]',
    position: 'bottom',
    showNext: true,
    nextText: 'Next',
    highlight: true,
  },
  {
    id: 'emb-open-stitch-editor',
    title: 'Open the Stitch Editor',
    description: 'Click **Next: Stitch Editor** to turn these layers into stitches you can edit.',
    target: '[data-tutorial="download-button"]',
    position: 'top',
    highlight: true,
    advanceOnTargetClick: true,
    actionRequired: true,
  },
  {
    id: 'emb-opening-editor',
    title: 'Opening the Stitch Editor',
    description: 'Saving your design and opening the editor. The walkthrough continues when it\'s ready.',
    target: null,
    position: 'center',
    waiting: true,
    waitForAction: 'editor-opened',
    autoAdvance: true,
  },
];

/** Index of the first lesson-specific step, once the prelude is prepended. */
export const FIRST_LESSON_STEP = PRELUDE_STEPS.length;

/**
 * Index of the picker, for entry points that are already an invitation to
 * choose an example — the generic welcome slide only repeats what the learner
 * just clicked.
 */
export const PICK_DESIGN_STEP = PRELUDE_STEPS.findIndex(
  (step) => step.id === 'emb-pick-design',
);

/**
 * Opens the lesson by naming what it teaches. The generic welcome can't do this
 * because it runs before the learner has picked which lesson they're taking.
 */
export const createLessonIntroStep = ({ title, summary, objectives }) => ({
  id: 'emb-lesson-intro',
  title,
  // One block, so the learner reads the aim of the lesson in a single beat
  // rather than clicking through it.
  description: `${summary}\n${objectives.map((line) => `• ${line}`).join('\n')}`,
  target: null,
  position: 'center',
  showNext: true,
  nextText: 'Start',
});

/** How layers map to threads — true of any design. */
export const LAYERS_INTRO_STEP = {
  id: 'emb-layers-intro',
  title: 'Your Layers',
  description: 'Each swatch is **one colour, one thread**. The machine stitches them in this order, pausing for a thread change between each.',
  target: 'button[aria-label="Show all layers"]',
  position: 'bottom',
  showNext: true,
  highlight: true,
};

/**
 * Says out loud that the design is already finished, before the lesson spends
 * ten minutes changing it.
 *
 * Every layer comes back from processing as an auto fill, which sews perfectly
 * well — so the satin work that follows is a refinement, not a repair. Left
 * unsaid, a learner reasonably concludes that an upload isn't usable until
 * they've done all of this by hand, which is the opposite of what we want them
 * to believe about the product.
 */
export const AUTO_FILL_BASELINE_STEP = {
  id: 'emb-already-stitchable',
  title: 'This Already Works',
  description: 'Every layer arrives as **auto fill** — solid rows of stitching. You could export this design right now and it would sew cleanly, without changing a thing.\n\nWhat follows is the step up from fine: **satin** along the narrow shapes, which sews smoother and catches the light in a way a fill can\'t.',
  target: null,
  position: 'center',
  showNext: true,
};

/**
 * Puts the Stitch panel away once a layer's stitching is settled.
 *
 * Nothing in the app closes it, so it sits there still offering satin options
 * after the satin is built, reading as unfinished work. Skipped if the learner
 * already closed it themselves.
 */
export const CLOSE_STITCH_PANEL_STEP = {
  id: 'emb-close-stitch-panel',
  title: 'Put the Panel Away',
  description: 'This layer is stitched, so close the **Stitch** panel — the **×** at its top right.',
  target: '[data-tutorial="stitch-panel-close"]',
  position: 'right',
  highlight: true,
  advanceOnTargetClick: true,
  skipIfMissing: 4000,
  waitForAction: 'emb-stitch-panel-closed',
  actionRequired: true,
};

/** Brings the full design back after work on an isolated layer. */
export const SHOW_ALL_LAYERS_STEP = {
  id: 'emb-show-all-layers',
  title: 'See the Whole Design',
  description: 'Click **Show all layers** to bring the design back.',
  target: 'button[aria-label="Show all layers"]',
  position: 'bottom',
  highlight: true,
  advanceOnTargetClick: true,
  waitForAction: 'emb-all-layers',
  actionRequired: true,
};

/**
 * Export and wrap-up. The mechanics are identical for every design; only the
 * closing recap differs, so each lesson passes the points it wants restated.
 */
export const createExportSteps = ({ learned }) => [
  {
    id: 'emb-export-open',
    title: 'Ready to Export',
    description: 'Click **Export Stitch** to turn your design into machine stitches.',
    target: 'button[aria-label="Export Stitch"]',
    position: 'left',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-export-open',
    actionRequired: true,
  },
  {
    id: 'emb-export-format',
    title: 'Check Format and Size',
    // Shrinking it is the one instruction here that isn't just orientation.
    // Designs default to 100mm and stitch count follows area, so a learner who
    // takes the default spends four times as long at the machine on a stitch-out
    // they only want to check. The aspect lock is on by default, so setting the
    // width is enough.
    description: 'The **format** is your machine\'s file type. **DST** suits most.\n\nThe **width and height** are the size it will stitch at — check them against your hoop, because resizing a stitch file afterwards degrades it.\n\nFor a first test, set the **width to 50 mm**. Stitching time follows area, so half the size is about **a quarter of the sewing** — and the height follows on its own.\n\nClick **Next — Preview & export**.',
    target: '[data-tutorial="export-next-preview"]',
    // The dialogue walks the learner down the panel: the format box, the two
    // size fields, the width on its own while it's being changed, then the
    // button that moves on.
    pageTargets: [
      { target: '[data-tutorial="export-format"]' },
      { multipleTargets: EXPORT_SIZE_TARGETS },
      { target: '[data-tutorial="export-width"]' },
      { target: '[data-tutorial="export-next-preview"]' },
    ],
    position: 'top',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-export-next',
    actionRequired: true,
  },
  {
    id: 'emb-generate',
    title: 'Generate the Stitches',
    description: 'Click **Generate Stitch File**. It works out every needle drop, so give it a moment.',
    target: '[data-tutorial="generate-stitch-file"]',
    position: 'top',
    highlight: true,
    advanceOnTargetClick: true,
    advanceDelay: 1200,
    waitForAction: 'emb-generated',
    actionRequired: true,
  },
  {
    id: 'emb-download',
    title: 'Download It',
    description: 'When the preview is ready, name your file and click **Download**. Stitch a test on scrap fabric before the real thing.',
    target: '[data-tutorial="export-download"]',
    position: 'top',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-downloaded',
    actionRequired: true,
  },
  {
    id: 'emb-complete',
    title: 'That\'s a Real Embroidery File',
    description: `**You just made a real embroidery file.**\n${learned.map((line) => `• ${line}`).join('\n')}\n\nThe same moves handle most designs — try your own artwork next.`,
    target: null,
    position: 'center',
    showNext: false,
    showSkip: false,
    showComplete: true,
    large: true,
  },
];
